import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, HourglassMedium, FilePdf, User, Calendar, MagnifyingGlass, X, ChatCircleDots, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SECTION_LABEL = {
  general: "General HR",
  nomina: "Nómina",
  calendar: "Calendario roles",
  certs: "Certificados",
  vacancies: "Vacantes",
  reimbursements: "Reembolsos",
};

/**
 * "Bitácora firmas" — panel para auditar todas las firmas digitales de PDFs
 * generadas por el módulo HR. Muestra firmadas y pendientes con filtros por
 * estado, sección y búsqueda por firmante.
 */
export default function SignaturesLogTab() {
  const [items, setItems] = useState([]);
  const [attSigs, setAttSigs] = useState([]);
  const [smsRuns, setSmsRuns] = useState({ runs: [], total_sms_today: 0 });
  const [filter, setFilter] = useState({ status: "all", section: "all", q: "" });
  const [preview, setPreview] = useState(null);   // whole signature doc for modal
  const [attPreview, setAttPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, sms, att] = await Promise.all([
        api.get("/hr/pdf-signatures?limit=500"),
        api.get("/hr/reminders/runs?days=7").catch(() => ({ data: { runs: [], total_sms_today: 0 } })),
        api.get("/hr/attendance/signatures?days=30").catch(() => ({ data: [] })),
      ]);
      setItems(data);
      setSmsRuns(sms.data);
      setAttSigs(att.data || []);
    } catch (e) { toast.error(apiError(e)); setItems([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    return items.filter((it) => {
      if (filter.status !== "all" && it.status !== filter.status) return false;
      if (filter.section !== "all" && it.section !== filter.section) return false;
      if (q && !(
        (it.signer_name || "").toLowerCase().includes(q) ||
        (it.generated_by || "").toLowerCase().includes(q) ||
        (it.token || "").toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [items, filter]);

  const stats = useMemo(() => ({
    total: items.length,
    signed: items.filter((x) => x.status === "signed").length,
    pending: items.filter((x) => x.status !== "signed").length,
  }), [items]);

  const chip = (v, active, l, key) => (
    <button
      key={`${key}-${v}`}
      data-testid={`sig-filter-${key}-${v}`}
      onClick={() => setFilter({ ...filter, [key]: v })}
      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${active === v ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
    >{l}</button>
  );

  return (
    <div className="space-y-4" data-testid="hr-signatures-log">
      {/* Panel SMS Cron — audita quiénes recibieron el SMS del 07:30/19:30 */}
      <div className="bg-gradient-to-br from-blue-950 via-slate-900 to-slate-800 border border-blue-800 rounded-lg p-4 text-white" data-testid="hr-sms-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ChatCircleDots size={20} weight="fill" className="text-amber-300" />
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-blue-200">Panel SMS del cron 07:30 / 19:30</p>
              <h3 className="font-heading font-black text-lg leading-tight">Auditoría de recordatorios enviados a supervisores</h3>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-amber-400 text-slate-950" data-testid="hr-sms-today-count">
              {smsRuns.total_sms_today} SMS hoy
            </span>
            <span className="text-[10px] text-blue-200">Últimos 7 días: {smsRuns.runs?.length || 0} corridas</span>
          </div>
        </div>
        {(smsRuns.runs || []).length === 0 ? (
          <p className="text-xs text-blue-200 italic text-center py-4">
            Aún no hay corridas del cron. Los recordatorios se disparan automáticamente a las 07:30 y 19:30 UTC cuando hay firmas pendientes.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="hr-sms-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-blue-300 border-b border-blue-800">
                  <th className="text-left py-1.5">Corrida</th>
                  <th className="text-center py-1.5">Slot</th>
                  <th className="text-center py-1.5">Pendientes</th>
                  <th className="text-center py-1.5">Notif. app</th>
                  <th className="text-center py-1.5">SMS</th>
                  <th className="text-left py-1.5">Supervisores contactados</th>
                </tr>
              </thead>
              <tbody>
                {smsRuns.runs.slice(0, 12).map((r, i) => (
                  <tr key={i} className="border-b border-blue-900 last:border-0" data-testid={`hr-sms-row-${i}`}>
                    <td className="py-1.5 font-mono text-blue-100">{(r.run_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-1.5 text-center">
                      {r.slot ? (
                        <span className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-400 text-slate-950">
                          {r.slot === "morning" ? "☀ 07:30" : r.slot === "evening" ? "🌙 19:30" : r.slot}
                        </span>
                      ) : (
                        <span className="text-[10px] text-blue-300 italic">manual</span>
                      )}
                    </td>
                    <td className="py-1.5 text-center font-mono">{r.pending_total ?? 0}</td>
                    <td className="py-1.5 text-center font-mono">{r.notified ?? 0}</td>
                    <td className="py-1.5 text-center">
                      <span className={`inline-flex items-center gap-1 font-mono font-bold ${(r.sms_sent || 0) > 0 ? "text-emerald-300" : "text-slate-500"}`}>
                        <PaperPlaneTilt size={10} weight="fill" /> {r.sms_sent ?? 0}
                      </span>
                    </td>
                    <td className="py-1.5 text-blue-100">
                      {(r.sms_recipients || []).length > 0 ? (
                        <span className="text-[11px]">{(r.sms_recipients || []).slice(0, 5).join(", ")}{(r.sms_recipients || []).length > 5 ? "…" : ""}</span>
                      ) : (
                        <span className="text-[10px] text-slate-500 italic">— sin supervisores con teléfono en nómina —</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[10px] text-blue-300 mt-2 italic">
          El SMS se envía solo desde el cron (no en pruebas manuales). Requiere que el supervisor tenga <code className="bg-blue-900 px-1 rounded">employee_id</code> vinculado a un <code className="bg-blue-900 px-1 rounded">hr_employee.phone</code>.
        </p>
      </div>

      {/* Header + stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard icon={FilePdf} label="Total firmas" value={stats.total} color="text-slate-900" />
        <StatCard icon={ShieldCheck} label="Firmados ✔" value={stats.signed} color="text-emerald-700" />
        <StatCard icon={HourglassMedium} label="Pendientes" value={stats.pending} color="text-amber-700" />
      </div>

      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid="sig-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Estado</span>
          {chip("all", filter.status, "Todos", "status")}
          {chip("pending", filter.status, "Pendientes", "status")}
          {chip("signed", filter.status, "Firmados", "status")}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Sección</span>
          {chip("all", filter.section, "Todas", "section")}
          {Object.entries(SECTION_LABEL).map(([v, l]) => chip(v, filter.section, l, "section"))}
        </div>
        <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
          <MagnifyingGlass size={14} className="text-slate-400" />
          <Input
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            placeholder="Buscar por firmante, generador o token…"
            className="h-8 text-xs"
            data-testid="sig-filter-search"
          />
          {filter.q && (
            <button onClick={() => setFilter({ ...filter, q: "" })}
                    className="text-slate-400 hover:text-red-600"
                    data-testid="sig-filter-search-clear">
              <X size={14} weight="bold" />
            </button>
          )}
          <span className="text-[10px] text-slate-500 shrink-0">
            <b className="text-slate-900" data-testid="sig-filter-count">{filtered.length}</b> de {stats.total}
          </span>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-8">Cargando…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">Sin firmas para los filtros seleccionados.</p>
        ) : (
          <table className="w-full text-xs" data-testid="sig-table">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="px-3 py-2 text-left">Sección</th>
                <th className="px-3 py-2 text-left">Fecha reporte</th>
                <th className="px-3 py-2 text-left">Generado por</th>
                <th className="px-3 py-2 text-left">Firmante</th>
                <th className="px-3 py-2 text-left">Firmado el</th>
                <th className="px-3 py-2 text-center">Estado</th>
                <th className="px-3 py-2 text-center">Acción</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.token} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sig-row-${it.token}`}>
                  <td className="px-3 py-2 font-semibold text-slate-800">{SECTION_LABEL[it.section] || it.section}</td>
                  <td className="px-3 py-2 tabular-nums font-mono text-[10px] text-slate-600">{it.report_date}</td>
                  <td className="px-3 py-2">
                    <p className="text-slate-800">{it.generated_by || "-"}</p>
                    <p className="text-[9px] text-slate-400">{it.generated_by_email}</p>
                  </td>
                  <td className="px-3 py-2">
                    {it.signer_name ? (
                      <>
                        <p className="text-slate-800 font-semibold">{it.signer_name}</p>
                        <p className="text-[9px] text-slate-400">{it.signer_email}</p>
                      </>
                    ) : (
                      <span className="text-slate-400 italic">Pendiente</span>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums font-mono text-[10px] text-slate-600">
                    {it.signed_at?.slice(0, 16).replace("T", " ") || "—"}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {it.status === "signed" ? (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                        <ShieldCheck size={10} weight="bold" /> Firmado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                        <HourglassMedium size={10} weight="bold" /> Pendiente
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {it.status === "signed" && (
                      <Button size="sm" variant="outline" onClick={() => setPreview(it)}
                              data-testid={`sig-preview-${it.token}`}
                              className="h-6 text-[10px]">Ver firma</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Preview modal */}
      {preview && (
        <Dialog open={true} onOpenChange={() => setPreview(null)}>
          <DialogContent className="bg-white max-w-md" data-testid="sig-preview-modal">
            <DialogHeader>
              <DialogTitle className="font-heading">Firma auditable · {SECTION_LABEL[preview.section]}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-xs">
              <p className="flex items-center gap-2"><Calendar size={12} className="text-slate-400" /> <b className="w-24">Reporte:</b> {preview.report_date}</p>
              <p className="flex items-center gap-2"><User size={12} className="text-slate-400" /> <b className="w-24">Firmado por:</b> {preview.signer_name} · {preview.signer_email}</p>
              <p className="flex items-center gap-2"><Calendar size={12} className="text-slate-400" /> <b className="w-24">Fecha firma:</b> {preview.signed_at?.slice(0, 16).replace("T", " ")}</p>
              <p className="font-mono text-[10px] text-slate-500">Token: {preview.token}</p>
              {preview.note && (
                <p className="text-slate-700 italic border-l-2 border-slate-200 pl-2">&ldquo;{preview.note}&rdquo;</p>
              )}
              <div className="pt-2">
                <img src={preview.signature_data_url} alt="firma" className="h-32 mx-auto bg-white border border-slate-200 rounded p-2" />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Firmas de asistencia diaria */}
      <div className="bg-white border border-emerald-200 rounded-lg overflow-hidden" data-testid="att-sig-panel">
        <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Firmas de asistencia diaria</p>
            <h3 className="font-heading font-black text-lg leading-tight text-slate-900">Últimos 30 días · pase de turno + kiosco + QR</h3>
          </div>
          <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-600 text-white" data-testid="att-sig-total">{attSigs.length} firmas</span>
        </div>
        {attSigs.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8 italic">
            Aún no hay firmas de asistencia. Ve a HR → Asistencia y usa el botón &quot;Firmar&quot; en cada fila antes de guardar.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="att-sig-table">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Colaborador</th>
                  <th className="px-3 py-2 text-left">Cuadr.</th>
                  <th className="px-3 py-2 text-left">Estado</th>
                  <th className="px-3 py-2 text-left">Origen</th>
                  <th className="px-3 py-2 text-left">Marcado a</th>
                  <th className="px-3 py-2 text-center">Match</th>
                  <th className="px-3 py-2 text-center">Ver</th>
                </tr>
              </thead>
              <tbody>
                {attSigs.map((s, i) => (
                  <tr key={`${s.employee_id}-${s.date}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`att-sig-row-${i}`}>
                    <td className="px-3 py-2 tabular-nums font-mono text-[10px] text-slate-700">{s.date}</td>
                    <td className="px-3 py-2">
                      <p className="font-semibold text-slate-800">{s.employee_name || s.employee_id}</p>
                      <p className="text-[9px] text-slate-400">{s.position} · DNI {s.dni || "-"}</p>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="inline-block h-5 w-5 rounded-full bg-blue-100 text-blue-900 text-[10px] font-bold leading-5">{s.crew || "?"}</span>
                    </td>
                    <td className="px-3 py-2 text-[10px] uppercase font-bold text-slate-700">{s.status}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                        s.source === "kiosk" ? "bg-purple-100 text-purple-800" :
                        s.source === "self" ? "bg-cyan-100 text-cyan-800" :
                        s.source === "bulk" ? "bg-emerald-100 text-emerald-800" :
                        "bg-slate-100 text-slate-700"
                      }`}>
                        {s.source === "kiosk" ? "Kiosco" : s.source === "self" ? "QR móvil" : s.source === "bulk" ? "Pase turno" : s.source}
                      </span>
                    </td>
                    <td className="px-3 py-2 tabular-nums font-mono text-[10px] text-slate-600">
                      {s.marked_at?.slice(0, 16).replace("T", " ") || "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {s.selfie_mismatch_flag ? (
                        <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-300 inline-flex items-center gap-0.5" title={`Rostro-match ${Math.round((s.selfie_similarity_score || 0) * 100)}% (posible suplantación)`} data-testid={`att-sig-flag-${i}`}>
                          ⚠ {Math.round((s.selfie_similarity_score || 0) * 100)}%
                        </span>
                      ) : s.selfie_similarity_score !== undefined && s.selfie_similarity_score !== null ? (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200" title="Rostro-match OK">
                          {Math.round(s.selfie_similarity_score * 100)}%
                        </span>
                      ) : <span className="text-[9px] text-slate-300 italic">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Button size="sm" variant="outline" onClick={() => setAttPreview(s)}
                              data-testid={`att-sig-preview-${i}`}
                              className="h-6 text-[10px]">
                        Ver firma{s.selfie_data_url ? " + selfie" : ""}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview firma de asistencia */}
      {attPreview && (
        <Dialog open={true} onOpenChange={() => setAttPreview(null)}>
          <DialogContent className="bg-white max-w-md" data-testid="att-sig-preview-modal">
            <DialogHeader>
              <DialogTitle className="font-heading">Firma de asistencia · {attPreview.date}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-xs">
              <p className="flex items-center gap-2"><User size={12} className="text-slate-400" /> <b className="w-24">Colaborador:</b> {attPreview.employee_name} · DNI {attPreview.dni || "-"}</p>
              <p className="flex items-center gap-2"><Calendar size={12} className="text-slate-400" /> <b className="w-24">Marcado a:</b> {attPreview.marked_at?.slice(0, 16).replace("T", " ")}</p>
              <p><b className="w-24 inline-block">Estado:</b> <span className="uppercase font-bold">{attPreview.status}</span></p>
              <p><b className="w-24 inline-block">Registrado por:</b> {attPreview.marked_by || "-"}</p>
              {attPreview.selfie_mismatch_flag && (
                <p className="p-2 rounded bg-red-50 border border-red-200 text-red-800 text-[11px] font-bold flex items-center gap-1" data-testid="att-sig-mismatch-alert">
                  ⚠ Rostro-match {Math.round((attPreview.selfie_similarity_score || 0) * 100)}% — posible suplantación. Verificar antes de aceptar.
                </p>
              )}
              <p className="font-mono text-[10px] text-slate-500 break-all">Audit hash: {attPreview.attendance_audit_hash || "—"}</p>
              <div className="pt-2 grid grid-cols-[1fr_auto] gap-3 items-start">
                <div>
                  <p className="text-[10px] uppercase font-bold text-slate-500 mb-1">Firma manuscrita</p>
                  <img src={attPreview.signature_data_url} alt="firma" className="h-32 w-full object-contain bg-white border border-slate-200 rounded p-2" />
                </div>
                {attPreview.selfie_data_url && (
                  <div>
                    <p className="text-[10px] uppercase font-bold text-purple-700 mb-1">Selfie forense</p>
                    <img src={attPreview.selfie_data_url} alt="selfie" className="h-32 w-32 object-cover rounded border border-purple-300" data-testid="att-sig-selfie-preview" />
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
        <Icon size={12} weight="duotone" className="text-blue-900" /> {label}
      </div>
      <p className={`font-heading font-black text-2xl mt-1 ${color}`}>{value}</p>
    </div>
  );
}
